// A course-only assignment has no session sheet to host the completion card,
// so the "practice" action opens this dialog instead: the server-evaluated
// checklist + the learner's PracticePanel for that assignment.
import { useT } from '../lib/i18n';
import React from 'react';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/features/shared/components/ui/dialog';
import { getApiErrorMessage } from '@/lib/apiError';
import { useAssignmentCompletion } from '../api/hooks';
import { CompletionChecklist } from './CompletionChecklist';
import { PracticePanel } from './PracticePanel';

interface Props { assignmentId: string | null; title?: string; onClose: () => void }

export const PracticeDialog: React.FC<Props> = ({ assignmentId, title, onClose }) => {
  const t = useT();
  const q = useAssignmentCompletion(assignmentId);
  return (
    <Dialog open={!!assignmentId} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="practice-dialog">
        <DialogHeader>
          <DialogTitle>{title || t('training:practice_title', 'Practical exercise')}</DialogTitle>
          <DialogDescription>{t('training:your_completion', 'Your completion')}</DialogDescription>
        </DialogHeader>
        {q.isLoading && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> {t('training:loading', 'Loading')}…</div>}
        {q.isError && <p className="text-sm text-red-600 dark:text-red-400">{getApiErrorMessage(q.error, t('training:completion_unavailable', 'Completion details are not available to you.'))}</p>}
        {q.data && (
          <div className="space-y-3 text-sm">
            <CompletionChecklist view={q.data} />
            <PracticePanel view={q.data} canSubmit idPrefix="pd" />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PracticeDialog;
